import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ScanFace, Wand2, ListChecks, ShieldCheck, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { PartnerSection } from "@/components/PartnerSection";
import { useAuth } from "@/context/AuthContext";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.6l6.3 5.2C41.4 36.4 44 30.7 44 24c0-1.3-.1-2.3-.4-3.5z"/>
  </svg>
);

const login = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const PROC_PREVIEW = [
  { name: "Rhinoplasty", cat: "Face" },
  { name: "Lip Filler", cat: "Face" },
  { name: "Jawline", cat: "Face" },
  { name: "Brow Lift", cat: "Face" },
  { name: "Liposuction", cat: "Body" },
  { name: "Tummy Tuck", cat: "Body" },
  { name: "BBL", cat: "Body" },
  { name: "Arm Lift", cat: "Body" },
];

const PLANS = [
  {
    name: "Free Trial",
    price: "$0",
    period: "one procedure",
    lookup: null,
    features: ["Preview 1 procedure", "AI before / after render", "Save your photo securely"],
    cta: "Start Free",
  },
  {
    name: "Pro",
    price: "$4.99",
    period: "per month",
    lookup: "pro_monthly",
    features: ["Unlimited AI previews", "Combine multiple procedures", "Build your surgical list", "High-resolution renders"],
    cta: "Choose Pro",
    highlight: false,
  },
  {
    name: "Premium",
    price: "$8.99",
    period: "per month",
    lookup: "premium_monthly",
    features: ["Everything in Pro", "Clinic recommendations", "Real cost estimates", "Priority AI rendering"],
    cta: "Go Premium",
    highlight: true,
  },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  return (
    <div className="relative min-h-screen bg-brand-cream text-brand-ink overflow-x-hidden">
      {/* Nav */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2" data-testid="brand-logo">
            <Sparkles className="w-5 h-5 text-brand-primary" strokeWidth={1.5} />
            <span className="font-heading text-xl">AuraLift</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/clinics" data-testid="nav-directory-link" className="hidden sm:block text-sm text-brand-slate hover:text-brand-primary transition-colors">Find a clinic</a>
            <a href="#for-clinics" data-testid="nav-clinics-link" className="hidden sm:block text-sm text-brand-slate hover:text-brand-primary transition-colors">For Clinics</a>
            <Button
              data-testid="nav-login-btn"
              onClick={login}
              className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2 transition-transform hover:-translate-y-0.5"
            >
              <GoogleIcon /> Continue with Google
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-24 px-6">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-sm mb-6">
              <ScanFace className="w-4 h-4" strokeWidth={1.5} /> AI-powered surgical visualization
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-6">
              See the <span className="text-brand-primary italic">after</span> before you decide.
            </h1>
            <p className="text-brand-slate text-lg leading-relaxed mb-8 max-w-md">
              Upload one photo and preview realistic results for face and body procedures. Build your surgical list and get real cost estimates — all in private.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <Button
                data-testid="hero-cta-btn"
                onClick={login}
                size="lg"
                className="rounded-full bg-brand-primary hover:bg-brand-primaryHover text-white gap-2 px-7 h-12 transition-transform hover:-translate-y-0.5"
              >
                Try it free <ArrowRight className="w-4 h-4" />
              </Button>
              <div className="flex items-center gap-2 text-sm text-brand-slate">
                <ShieldCheck className="w-4 h-4 text-brand-primary" strokeWidth={1.5} /> Private & secure
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="relative"
          >
            <div className="p-3 bg-white rounded-3xl border border-brand-border/60 shadow-[0_8px_30px_rgba(74,93,82,0.10)]">
              <BeforeAfterSlider
                before="https://images.unsplash.com/photo-1602033350291-a9ab8d800269?w=800&q=80"
                after="https://images.unsplash.com/photo-1541715301255-12a4839b424a?w=800&q=80"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 hidden md:flex items-center gap-2 px-4 py-3 bg-white rounded-2xl shadow-lg border border-brand-border/60">
              <Wand2 className="w-4 h-4 text-brand-accent" strokeWidth={1.5} />
              <span className="text-sm font-medium">Drag to compare</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Procedures */}
      <section className="py-24 px-6 bg-brand-sand/60">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-3">A full menu of procedures</h2>
          <p className="text-brand-slate mb-12 max-w-lg">From subtle refinements to full transformations — face and body, visualized instantly.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {PROC_PREVIEW.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="p-8 bg-white rounded-2xl border border-brand-border/60 hover:-translate-y-1 transition-transform duration-300 shadow-[0_4px_20px_rgba(74,93,82,0.05)]"
              >
                <span className="text-xs uppercase tracking-widest text-brand-accent">{p.cat}</span>
                <p className="font-heading text-xl mt-2">{p.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner clinics */}
      <PartnerSection />

      {/* Pricing */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight mb-3">Simple, honest pricing</h2>
            <p className="text-brand-slate">Start free. Upgrade whenever you're ready.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 items-stretch">
            {PLANS.map((plan) => (
              <div
                key={plan.name}
                data-testid={`plan-card-${plan.name.toLowerCase().replace(" ", "-")}`}
                className={`relative flex flex-col p-8 rounded-3xl border transition-transform duration-300 hover:-translate-y-1 ${
                  plan.highlight
                    ? "bg-brand-primary text-white border-brand-primary shadow-[0_12px_40px_rgba(74,93,82,0.25)]"
                    : "bg-white border-brand-border/60 shadow-[0_4px_20px_rgba(74,93,82,0.05)]"
                }`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-brand-accent text-white text-xs font-medium">Most popular</span>
                )}
                <h3 className={`font-heading text-2xl mb-1 ${plan.highlight ? "text-white" : ""}`}>{plan.name}</h3>
                <div className="flex items-end gap-1 mb-6">
                  <span className="font-heading text-4xl">{plan.price}</span>
                  <span className={`text-sm mb-1 ${plan.highlight ? "text-white/70" : "text-brand-slate"}`}>/ {plan.period}</span>
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlight ? "text-brand-accent" : "text-brand-primary"}`} />
                      <span className={plan.highlight ? "text-white/90" : "text-brand-slate"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  data-testid={`plan-cta-${plan.name.toLowerCase().replace(" ", "-")}`}
                  onClick={login}
                  className={`rounded-full h-11 transition-transform hover:-translate-y-0.5 ${
                    plan.highlight
                      ? "bg-white text-brand-primary hover:bg-white/90"
                      : "bg-brand-primary text-white hover:bg-brand-primaryHover"
                  }`}
                >
                  {plan.cta}
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="py-10 px-6 border-t border-brand-border/60">
        <div className="max-w-6xl mx-auto flex flex-col items-center gap-4 text-sm text-brand-slate">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-primary" strokeWidth={1.5} />
              <span className="font-heading text-base text-brand-ink">AuraLift</span>
            </div>
            <p>Visualizations are AI-generated and for illustration only. Consult a licensed surgeon.</p>
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 w-full pt-4 border-t border-brand-border/60 text-xs">
            <p data-testid="copyright-notice">© {new Date().getFullYear()} AuraLift. All rights reserved. Unauthorised copying or reuse of this site, its design or software is prohibited.</p>
            <a href="/legal" data-testid="footer-legal-link" className="hover:text-brand-primary transition-colors">Terms &amp; Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
